/**
 * CR313: Rollouts Tab
 * 
 * Shows only rollout-type flags with quick summary.
 */

import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Settings2 } from 'lucide-react';
import { FeatureFlagDrawer } from './FeatureFlagDrawer';
import type { FeatureFlagState, UseFeatureFlagsAdminResult } from '@/hooks/admin/useFeatureFlagsAdmin';
import type { RolloutValue } from '@/lib/admin/featureFlagsRegistry';
import { cn } from '@/lib/utils';

interface FeatureFlagsRolloutsTabProps {
  adminHook: UseFeatureFlagsAdminResult;
}

function getRolloutDetails(value: unknown): { mode: string; percent?: number; allowlistCount?: number } {
  if (!value || typeof value !== 'object') {
    return { mode: 'Invalid' };
  }
  const v = value as RolloutValue;
  return {
    mode: v.mode || 'OFF',
    percent: v.mode === 'PERCENT' ? v.percent : undefined,
    allowlistCount: v.mode === 'ALLOWLIST' ? v.allowlist?.length : undefined,
  };
}

function getModeColor(mode: string): string {
  switch (mode) {
    case 'ON':
      return 'bg-green-500/10 text-green-600 border-green-500/20';
    case 'OFF':
      return 'bg-gray-500/10 text-gray-600 border-gray-500/20';
    case 'PERCENT':
      return 'bg-blue-500/10 text-blue-600 border-blue-500/20';
    case 'ALLOWLIST':
      return 'bg-purple-500/10 text-purple-600 border-purple-500/20';
    default:
      return '';
  }
}

export function FeatureFlagsRolloutsTab({ adminHook }: FeatureFlagsRolloutsTabProps) {
  const { flags, draftValues, updateDraft, deleteOverride } = adminHook;
  const [drawerFlag, setDrawerFlag] = useState<FeatureFlagState | null>(null);
  
  const rolloutFlags = flags.filter(f => f.registryEntry?.type === 'rollout');
  
  const handleDeleteOverride = async () => {
    if (!drawerFlag) return;
    const success = await deleteOverride(drawerFlag.key);
    if (success) {
      setDrawerFlag(null);
    }
  };
  
  if (rolloutFlags.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        No rollout flags configured
      </div>
    );
  }
  
  return (
    <div className="p-4">
      <div className="border rounded-lg overflow-hidden">
        {/* Header */}
        <div className="grid grid-cols-[1fr_100px_100px_100px_100px_48px] gap-4 px-4 py-2 bg-muted/50 text-xs font-medium text-muted-foreground">
          <div>Flag</div>
          <div>Status</div>
          <div>Mode</div>
          <div>Percent</div>
          <div>Allowlist</div>
          <div></div>
        </div>
        
        {/* Rows */}
        {rolloutFlags.map(flag => {
          const draftValue = draftValues.get(flag.key);
          const details = getRolloutDetails(draftValue);
          
          return (
            <div
              key={flag.key}
              className="grid grid-cols-[1fr_100px_100px_100px_100px_48px] gap-4 px-4 py-3 border-t items-center hover:bg-muted/30 transition-colors"
            >
              {/* Label + key */}
              <div className="min-w-0">
                <div className="font-medium text-sm truncate">
                  {flag.registryEntry?.label}
                </div>
                <div className="text-xs text-muted-foreground font-mono truncate">
                  {flag.key}
                </div>
              </div>
              
              {/* Status */}
              <div>
                <Badge 
                  variant={flag.isOverridden ? 'default' : 'secondary'}
                  className={cn(
                    'text-xs',
                    flag.isOverridden && 'bg-amber-500 hover:bg-amber-500'
                  )}
                >
                  {flag.isOverridden ? 'Overridden' : 'Default'}
                </Badge>
              </div>
              
              {/* Mode */}
              <div>
                <Badge variant="outline" className={cn('text-xs', getModeColor(details.mode))}>
                  {details.mode}
                </Badge>
              </div>
              
              {/* Percent */}
              <div className="text-sm">
                {details.percent !== undefined ? `${details.percent}%` : '—'}
              </div>
              
              {/* Allowlist */}
              <div className="text-sm">
                {details.allowlistCount !== undefined ? details.allowlistCount : '—'}
              </div>
              
              {/* Edit */}
              <div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setDrawerFlag(flag)}
                >
                  <Settings2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Drawer */}
      <FeatureFlagDrawer
        flag={drawerFlag}
        draftValue={drawerFlag ? draftValues.get(drawerFlag.key) : undefined}
        open={!!drawerFlag}
        onOpenChange={(open) => !open && setDrawerFlag(null)}
        onValueChange={(value) => drawerFlag && updateDraft(drawerFlag.key, value)}
        onDeleteOverride={handleDeleteOverride}
      />
    </div>
  );
}
